import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from '@/generated/api/schema';
import { getAccessTokenSilent } from '@/core/auth/tokenProvider';
import { newCorrelationId } from './correlationId';
import { ApiError } from '@/models/apiError';
import type { ApiErrorBody } from '@/models/apiError';

/**
 * Singleton openapi-fetch client typed against the generated OpenAPI schema.
 *
 * `baseUrl` is `window.location.origin`, not `runtimeConfig.apiBaseUrl`: the generated `paths`
 * already carry the full `/api/v1/...` prefix, so prepending `apiBaseUrl` (also `/api/v1`) would
 * double it into `/api/v1/api/v1/...` and silently 404. An absolute origin also avoids `fetch`
 * throwing on relative URLs outside a browser document (e.g. Vitest/jsdom).
 *
 * `fetch` is a thin wrapper re-reading `globalThis.fetch` per call, not a captured reference:
 * this singleton evaluates at import time, before MSW patches the global in test setup — a
 * captured reference would freeze `apiClient` onto the pre-patch `fetch` and MSW could never
 * intercept it.
 */
export const apiClient = createClient<paths>({
  baseUrl: window.location.origin,
  fetch: (...args: Parameters<typeof fetch>) => globalThis.fetch(...args),
});

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const token = await getAccessTokenSilent();
    if (token !== null) {
      request.headers.set('Authorization', `Bearer ${token}`);
    }
    request.headers.set('X-Correlation-Id', newCorrelationId());
    return request;
  },

  async onResponse({ response }) {
    if (!response.ok) {
      const correlationId = response.headers.get('X-Correlation-Id');

      let body: ApiErrorBody | null = null;
      try {
        body = (await response.clone().json()) as ApiErrorBody;
      } catch {
        // Response body is not JSON
      }

      if (body === null) {
        throw new ApiError(
          response.status,
          {
            error: response.statusText,
            code: 'UNKNOWN',
            timestamp: new Date().toISOString(),
            path: new URL(response.url).pathname,
          },
          correlationId,
        );
      }

      throw new ApiError(response.status, body, correlationId);
    }
    return response;
  },
};

apiClient.use(authMiddleware);
